import {
  completedExerciseIndices,
  getLearningLesson,
  learningLessons,
  lessonForReviewCategory,
  recommendedLearningLesson,
  openingLessonManifest,
} from "@chesshotel/types";
import openings from "./journeyOpenings.json";
import { linesComplete } from "./learningProgress";
import { LearningOverview, LearningStep } from "./learningTypes";

export const journeyChapters = [
  {
    key: "basics",
    title: "foundationLessons.basics",
    slugs: learningLessons
      .filter((l) => l.group === "basics")
      .map((l) => l.slug),
  },
  {
    key: "material",
    title: "foundationLessons.tactics",
    slugs: ["hanging-pieces", "forks", "pins"],
  },
  {
    key: "position",
    title: "foundationLessons.strategy",
    slugs: ["development", "centre-control", "king-safety", "piece-activity"],
  },
  {
    key: "tactics",
    title: "foundationLessons.tactics",
    difficulty: "intermediate",
    slugs: ["skewers", "discovered-attacks", "back-rank-mates"],
  },
  {
    key: "endgames",
    title: "foundationLessons.endgames",
    slugs: ["basic-checkmates", "king-and-pawn"],
  },
];
export const journeySlugs = journeyChapters.flatMap((c) => c.slugs);
export function lessonIsComplete(data: LearningOverview, slug: string) {
  const lesson = getLearningLesson(slug);
  return (
    !!lesson &&
    completedExerciseIndices(
      data.lessonProgress?.find((p) => p.slug === slug)?.completedExercises,
      lesson.exercises.length,
    ).length === lesson.exercises.length
  );
}
export function learningJourney(data: LearningOverview) {
  const progress = data.lessonProgress || [];
  // Use saved placement/history; never treat the default rating as a skill assessment.
  const knowsRules = data.startingPoint
    ? data.startingPoint !== "new"
    : progress.some(
        (p) =>
          getLearningLesson(p.slug)?.group !== "basics" &&
          p.completedExercises.length > 0,
      ) ||
      !!data.openingProgress?.some(
        (p) => completedExerciseIndices(p.completedLines, 1000).length > 0,
      ) ||
      (data.nextStep.kind === "lesson" &&
        !!data.nextStep.lessonSlug &&
        getLearningLesson(data.nextStep.lessonSlug)?.group !== "basics");
  const initial =
    data.startingPoint === "regular"
      ? recommendedLearningLesson({ progress: [], startingPoint: "regular" })
          ?.slug
      : knowsRules
        ? "hanging-pieces"
        : journeySlugs[0];
  const start = Math.max(0, journeySlugs.indexOf(initial || journeySlugs[0]));
  const unfinished = journeySlugs.find(
    (slug, i) => i >= start && !lessonIsComplete(data, slug),
  );
  const resumed = journeySlugs.find(
    (slug) =>
      !lessonIsComplete(data, slug) &&
      (progress.find((p) => p.slug === slug)?.completedExercises.length || 0) >
        0 &&
      !(knowsRules && getLearningLesson(slug)?.group === "basics"),
  );
  const current = resumed || unfinished;
  const status = (slug: string) =>
    lessonIsComplete(data, slug)
      ? "completed"
      : slug === current
        ? "current"
        : journeySlugs.indexOf(slug) < start
          ? "optional"
          : "locked";
  return { current, status, start };
}
export function journeyNextStep(data: LearningOverview): LearningStep {
  const step = data.nextStep;
  // Keep actual personal recommendations and active/due exercises authoritative.
  if (step.personal || ["personal", "resume", "revisit"].includes(step.kind))
    return step;
  const current = learningJourney(data).current;
  return current
    ? { kind: "lesson", lessonSlug: current }
    : { kind: "collection" };
}
export function insightLesson(step: LearningStep) {
  return step.lessonSlug || lessonForReviewCategory(step.category)?.slug;
}
export function openingIsComplete(data: LearningOverview, slug: string) {
  const course = openingLessonManifest[slug];
  if (!course) return false;
  const row = data.openingProgress?.find((p) => p.slug === slug);
  return (
    linesComplete(
      row?.completedLines,
      row?.practiceLineProgress,
      course.practice,
    ) &&
    (row?.challengeCompleted === true ||
      linesComplete(
        row?.completedChallengeLines,
        row?.challengeLineProgress,
        course.challenge,
      ))
  );
}
export function currentOpening(data: LearningOverview) {
  return learningJourney(data).current
    ? undefined
    : openings.find((o) => !openingIsComplete(data, o.slug));
}
export function pathReturnHref(completed?: unknown) {
  return typeof completed === "string" &&
    (getLearningLesson(completed) || openingLessonManifest[completed])
    ? `/learn?completed=${encodeURIComponent(completed)}#path-current`
    : "/learn#path-current";
}

/** An optional insight may be completed ahead of the path; it must not claim to unlock an already available step. */
export function unlockedByCompletion(
  data: LearningOverview,
  completed?: string,
) {
  if (!completed) return undefined;
  const journey = learningJourney(data);
  const completedIndex = journeySlugs.indexOf(completed);
  if (lessonIsComplete(data, completed) && completedIndex >= journey.start) {
    if (
      journey.current &&
      completedIndex < journeySlugs.indexOf(journey.current)
    )
      return journey.current;
    if (!journey.current) return currentOpening(data)?.slug;
  }
  const opening = currentOpening(data);
  if (
    opening &&
    openingIsComplete(data, completed) &&
    openings.findIndex((o) => o.slug === completed) <
      openings.findIndex((o) => o.slug === opening.slug)
  )
    return opening.slug;
  return undefined;
}
